import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "../components/supabaseClient";
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  format,
  isSameMonth,
  isToday,
} from "date-fns";
import { useNavigate } from "react-router-dom";

const Colors = {
  bg: "#0f0f14",
  card: "#1a1a22",
  text: "#ffffff",
  muted: "#888",
  green: "#1f8f4e",
  orange: "#c46a2b",
  red: "#8b2f2f",
  mid: "#3f7fa6",
};

const emojiMap = {
  gym: "🏋️‍♂️",
  run: "🏃",
  sport: "⚽️",
  swim: "🏊",
  walk: "🚶",
  bike: "🚴🏻",
  hyrox: "⚡️",
};

const mainButton = {
  border: "none",
  borderRadius: 12,
  padding: "12px 20px",
  background: "#ddb52f",
  color: "#4e0329",
  fontWeight: "bold",
  fontSize: 16,
  marginBottom: 20,
  marginTop: 20,
};

const DAY_BOX_SIZE = 80;

const {
  data: { user },
} = await supabase.auth.getUser();

export default function Logs() {
  const [entries, setEntries] = useState({});
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const navigate = useNavigate();

  //const [retroLogging, setRetroLogging] = useState(false);

  const fetchEntries = useCallback(async () => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);

    const { data, error } = await supabase
      .from("daily_entries")
      .select("entry_date, structured, targets")
      .eq("user_id", user.id)
      .gte("entry_date", start.toISOString())
      .lte("entry_date", end.toISOString());

    if (error) {
      console.error(error);
      return;
    }

    const mapped = {};

    data.forEach((entry) => {
      //const dayKey = format(new Date(entry.created_at), "yyyy-MM-dd");
      const dayKey = entry.entry_date;

      if (!mapped[dayKey]) {
        mapped[dayKey] = [];
      }

      mapped[dayKey].push({
        structured: entry.structured,
        id: entry.id,
      });
    });

    setEntries(mapped);
  }, [currentMonth]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  function getDayColor(entry, currentDay) {
    if (entry.length < 1) return Colors.card;

    if (isToday(currentDay)) {
      return Colors.green;
    }

    return Colors.mid;
  }

  function getGlow(color) {
    if (color === Colors.green) return "none"; //"0 0 12px rgba(31,143,78,0.9)";
    return "none";
  }

  const handleDeleteWorkout = async (id) => {
    const confirmDelete = window.confirm("Delete this workout?");

    if (!confirmDelete) return;

    const { error } = await supabase
      .from("daily_entries")
      .delete()
      .eq("id", id);

    if (error) {
      console.error(error);
      return;
    }

    // Remove from selected day
    setSelectedDay((prev) => ({
      ...prev,
      entry: prev.entry.filter((e) => e.id !== id),
    }));

    // Refresh calendar
    fetchEntries();
  };

  function returnTrainingTypeEmojis(dayEntries, day) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const entryDay = new Date(day);
    entryDay.setHours(0, 0, 0, 0);

    // Future days: show nothing
    if (entryDay > today) {
      return null;
    }

    // No training
    if (!dayEntries || dayEntries.length === 0) {
      // Show sleep only for past days, NOT today
      if (entryDay < today) {
        return <span style={{ fontSize: 20 }}>😴</span>;
      }

      return null;
    }

    const types = [];

    dayEntries.forEach((entry) => {
      const activities = entry?.structured?.activities || [];

      activities.forEach((activity) => {
        if (activity.training_type && !types.includes(activity.training_type)) {
          types.push(activity.training_type);
        }
      });
    });

    return (
      <>
        {types.map((type) => (
          <span
            key={type}
            style={{
              fontSize: 24,
            }}
          >
            {emojiMap[type]}
          </span>
        ))}
      </>
    );
  }

  function getWeeklyTrainingTotals(weekStart) {
    const totals = {};

    for (let i = 0; i < 7; i++) {
      const date = addDays(weekStart, i);
      const dayKey = format(date, "yyyy-MM-dd");
      const dayEntries = entries[dayKey] || [];

      dayEntries.forEach((entry) => {
        const activities = entry?.structured?.activities || [];
        var gymToday = false;

        activities.forEach((activity) => {
          const type = activity.training_type;
          var duration = Number(activity.duration_minutes) || 0;

          if (type === "gym" && !gymToday) {
            duration = 60;
            gymToday = true;
          }

          if (!type || duration <= 0) return;

          if (!totals[type]) {
            totals[type] = 0;
          }

          totals[type] += duration;
        });
      });
    }

    return totals;
  }

  function formatHours(minutes) {
    const hours = minutes / 60;

    if (Number.isInteger(hours)) {
      return `${hours}h`;
    }

    return `${hours.toFixed(1)}h`;
  }

  function renderCells() {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    //const startDate = startOfWeek(monthStart);
    //const endDate = endOfWeek(monthEnd);

    const startDate = startOfWeek(monthStart, {
      weekStartsOn: 1,
    });

    const endDate = endOfWeek(monthEnd, {
      weekStartsOn: 1,
    });

    const rows = [];
    let days = [];
    let day = startDate;

    /*while (day <= endDate) {
      for (let i = 0; i < 7; i++) {*/
    while (day <= endDate) {
      const weekStart = day;

      for (let i = 0; i < 7; i++) {
        const currentDay = day;
        const formattedDate = format(currentDay, "d");
        const dayKey = format(currentDay, "yyyy-MM-dd");
        const dayEntries = entries[dayKey] || [];

        const color = getDayColor(dayEntries, currentDay);

        const glow = getGlow(color);

        days.push(
          <motion.div
            key={dayKey}
            layoutId={dayKey}
            onClick={() =>
              /*setSelectedDay({
                dayKey,
                entry,
                formattedDate,
                fullDate: format(currentDay, "MMMM d, yyyy"),
              })*/
              setSelectedDay({
                dayKey,
                entries: dayEntries,
                formattedDate,
                fullDate: format(currentDay, "MMMM d, yyyy"),
              })
            }
            style={{
              width: "100%",
              height: DAY_BOX_SIZE,
              padding: 8,
              borderRadius: 12,
              background: color,
              opacity: isSameMonth(currentDay, monthStart) ? 1 : 0.35,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              position: "relative",
              cursor: "pointer",
              boxShadow: glow,
            }}
            whileTap={{ scale: 0.94 }}
          >
            <span style={{ fontSize: 12, fontWeight: 600 }}>
              {formattedDate}
            </span>

            <div
              style={{
                position: "absolute",
                bottom: 6,
                right: 6,
                display: "flex",
                gap: 6,
                fontSize: 27,
                alignItems: "center",
              }}
            >
              {
                isSameMonth(currentDay, monthStart) && (
                  <div
                    style={{
                      position: "absolute",
                      bottom: 6,
                      right: 6,
                      display: "flex",
                      gap: 3,
                      fontSize: 24,
                      alignItems: "center",
                      flexDirection: "column",
                    }}
                  >
                    {returnTrainingTypeEmojis(dayEntries, currentDay)}
                  </div>
                )

                /*returnTrainingTypeEmoji(entry, currentDay)*/
              }
            </div>
          </motion.div>
        );

        day = addDays(day, 1);
      }

      const weeklyTotals = getWeeklyTrainingTotals(weekStart);

      days.push(
        <div
          key={`week-total-${weekStart.toISOString()}`}
          style={{
            height: DAY_BOX_SIZE,
            borderRadius: 12,
            background: "rgb(72 72 83)", //Colors.card,
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            padding: "6px 0px",
            fontSize: 13,
            width: "85%",
            marginLeft: 15,
          }}
        >
          {/*   {Object.entries(weeklyTotals).map(([type, minutes]) => (*/}

          {Object.entries(weeklyTotals)
            .sort(([, a], [, b]) => b - a)
            .map(([type, minutes]) => (
              <div
                key={type}
                style={{
                  display: "flex",
                  alignItems: "center",
                  whiteSpace: "nowrap",
                  marginLeft: 4,
                }}
              >
                <span style={{ fontSize: 17 }}>{emojiMap[type] || "🏋️‍♂️"}</span>

                <span>{formatHours(minutes)}</span>
              </div>
            ))}
        </div>
      );

      rows.push(
        <div
          key={day.toString()}
          style={{
            display: "grid",
            //gridTemplateColumns: "repeat(7, 1fr)", CHG8SEP
            gridTemplateColumns: "repeat(7, 1fr) 1.7fr",
            gap: 6,
            marginBottom: 6,
          }}
        >
          {days}
        </div>
      );

      days = [];
    }

    return rows;
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: Colors.bg,
        color: Colors.text,
        padding: 5,
        paddingTop: 60,
        overflowX: "auto",
      }}
    >
      <h2 style={{ marginBottom: 12 }}>{format(currentMonth, "MMMM yyyy")}</h2>

      <div style={{ marginBottom: 16 }}>
        <button onClick={() => setCurrentMonth(addDays(currentMonth, -30))}>
          ◀
        </button>
        <button
          style={{ marginLeft: 10 }}
          onClick={() => setCurrentMonth(addDays(currentMonth, 30))}
        >
          ▶
        </button>
      </div>

      <div
        style={{
          display: "grid",
          //gridTemplateColumns: "repeat(7, 1fr)", CHG8SEP
          gridTemplateColumns: "repeat(7, 1fr) 1.7fr",
          gap: 6,
          marginBottom: 8,
          textAlign: "center",
          fontWeight: 600,
          color: "rgba(255,255,255,0.8)",
          fontSize: 13,
        }}
      >
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun", "Tot(h)"].map(
          (day) => (
            <div key={day}>{day}</div>
          )
        )}
      </div>

      {renderCells()}

      <AnimatePresence>
        {selectedDay && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.75)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1000,
            }}
            onClick={() => setSelectedDay(null)}
          >
            <motion.div
              layoutId={selectedDay.dayKey}
              onClick={(e) => e.stopPropagation()}
              style={{
                //background: getDayColor(selectedDay.entry),
                background: getDayColor(selectedDay.entries),
                borderRadius: 20,
                padding: 20,
                width: "92%",
                maxWidth: 420,
                color: "#fff",
                //boxShadow: getGlow(getDayColor(selectedDay.entry)),
                boxShadow: getGlow(getDayColor(selectedDay.entries)),
                maxHeight: "80vh",
                overflowY: "auto",
              }}
            >
              <h3>{selectedDay.fullDate}</h3>

              {selectedDay.entries && selectedDay.entries.length > 0 ? (
                <>
                  <h4 style={{ marginTop: 12 }}>Training</h4>

                  {selectedDay.entries.map((entry, entryIndex) => (
                    <div key={entry.id || entryIndex}>
                      {entry.structured?.activities?.length > 0 ? (
                        entry.structured.activities.map((a, i) => {
                          let emoji = "🏋️‍♂️";

                          switch (a.training_type) {
                            case "run":
                              emoji = "🏃";
                              break;
                            case "sport":
                              emoji = "⚽️";
                              break;
                            case "swim":
                              emoji = "🏊";
                              break;
                            case "walk":
                              emoji = "🚶";
                              break;
                            case "gym":
                              emoji = "🏋️‍♂️";
                              break;
                            case "bike":
                              emoji = "🚴🏻";
                              break;
                            case "hyrox":
                              emoji = "⚡️";
                              break;
                            default:
                              emoji = "🏋️‍♂️";
                          }

                          return (
                            <div
                              key={i}
                              style={{
                                background: "rgba(0,0,0,0.25)",
                                borderRadius: 14,
                                padding: 14,
                                marginBottom: 10,
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  marginBottom: 6,
                                }}
                              >
                                {a.training_type === "hyrox" && (
                                  <strong style={{ fontSize: 15 }}>
                                    {a.training_type} {emoji}
                                  </strong>
                                )}

                                {a.training_type !== "hyrox" && (
                                  <strong style={{ fontSize: 15 }}>
                                    {a.activity_type || a.training_type} {emoji}
                                  </strong>
                                )}
                              </div>

                              {a.training_type === "gym" && (
                                <div style={{ fontSize: 13, color: "#ddd" }}>
                                  {a.anchor_metric?.weight != null && (
                                    <span>{a.anchor_metric.weight} kg</span>
                                  )}

                                  {a.anchor_metric?.sets != null && (
                                    <>
                                      <span style={{ margin: "0 5px" }}>•</span>
                                      <span>{a.anchor_metric.sets} sets</span>
                                    </>
                                  )}

                                  {a.anchor_metric?.reps != null && (
                                    <>
                                      <span style={{ margin: "0 5px" }}>•</span>
                                      <span>{a.anchor_metric.reps} reps</span>
                                    </>
                                  )}
                                </div>
                              )}

                              {((a.training_type === "run" ||
                                a.training_type === "swim" ||
                                a.training_type === "walk" ||
                                a.training_type === "bike") &&
                                a.anchor_metric?.cardio) ||
                                (a.duration_minutes > 0 && (
                                  <div style={{ fontSize: 13, color: "#ddd" }}>
                                    {formatHours(a.duration_minutes)}
                                  </div>
                                ))}

                              {a.training_type === "sport" && a.notes && (
                                <div style={{ fontSize: 13, color: "#ddd" }}>
                                  {a.notes}
                                </div>
                              )}

                              {/* 
                              {a.training_type === "hyrox" && (
                                <div style={{ fontSize: 13, color: "#ddd" }}>
                                  {formatHours(a.duration_minutes)}
                                </div>
                              )} */}

                              {a.notes && a.training_type !== "sport" && (
                                <div
                                  style={{
                                    fontSize: 12,
                                    marginTop: 7,
                                    color: "#aaa",
                                  }}
                                >
                                  {a.notes}
                                </div>
                              )}

                              <div>
                                <button
                                  onClick={() => handleDeleteWorkout(entry.id)}
                                  style={{
                                    background: "transparent",
                                    border: "none",
                                    color: "#999",
                                    fontSize: 18,
                                    cursor: "pointer",
                                    padding: 0,
                                    marginLeft: 10,
                                  }}
                                >
                                  ✕
                                </button>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <p style={{ opacity: 0.7 }}>No training logged.</p>
                      )}
                    </div>
                  ))}
                </>
              ) : (
                <p>No data for this day.</p>
              )}

              {selectedDay &&
                new Date(`${selectedDay.dayKey}T00:00:00`) <
                  new Date(new Date().setHours(0, 0, 0, 0)) && (
                  <button
                    style={mainButton}
                    onClick={() => {
                      navigate(`/checkin?date=${selectedDay.dayKey}`);
                    }}
                  >
                    ＋ Retro log
                  </button>
                )}

              <button
                onClick={() => setSelectedDay(null)}
                style={{
                  marginTop: 20,
                  width: "100%",
                  padding: 12,
                  border: "none",
                  borderRadius: 12,
                  background: "#000",
                  color: "#fff",
                  fontWeight: "bold",
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
